import { Alert, Card, Checkbox, Divider, Flex, Image } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledReviewSubmit } from './ReviewSubmit.styles';
import { CardRows } from './components/CardRows/CardRows';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';

export const ReviewSubmit = (): JSX.Element => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const { deviceData, issueDescriptionData, issueDetailsData, handleAddServiceCaseBack, handleSubmitRequest } =
    useServiceRequestContext();

  const deviceAddress = useMemo(
    () =>
      [issueDetailsData.address, `${issueDetailsData.zipCode} ${issueDetailsData.city}`, issueDetailsData.state]
        .filter(Boolean)
        .join(', '),
    [issueDetailsData.address, issueDetailsData.zipCode, issueDetailsData.city, issueDetailsData.state]
  );

  return (
    <StyledReviewSubmit className="review-submit">
      <h2 className="review-submit__title">{t('service.reviewSubmit.title')}</h2>

      <Flex className="review-submit__cards" gap={24} vertical>
        {/* Device */}
        <Card className="review-submit__card" title={t('service.reviewSubmit.device.title')}>
          {deviceData.identifiedViaHelper ? (
            <Flex gap={8} vertical>
              <Alert
                type="info"
                icon={<SvgIcon name="information" className="review-submit__no-serial-alert-icon" />}
                showIcon
                className="review-submit__no-serial-alert"
                message={t('service.reviewSubmit.device.noSerialWarning')}
              />
              {deviceData.identifiedViaHelper.deviceType && (
                <div>
                  <p className="review-submit__device-info-label">{t('service.reviewSubmit.device.title')}</p>
                  <p className="review-submit__device-info">{deviceData.identifiedViaHelper.deviceType}</p>
                </div>
              )}
              {deviceData.identifiedViaHelper.deviceName && (
                <div>
                  <p className="review-submit__device-info-label">{t('service.reviewSubmit.device.name')}</p>
                  <p className="review-submit__device-info">{deviceData.identifiedViaHelper.deviceName}</p>
                </div>
              )}
              {deviceData.identifiedViaHelper.deviceImage && (
                <Image
                  className="review-submit__device-image"
                  wrapperClassName="review-submit__device-image-wrapper"
                  src={URL.createObjectURL(deviceData.identifiedViaHelper.deviceImage)}
                  alt={deviceData.identifiedViaHelper.deviceName ?? 'device'}
                />
              )}
            </Flex>
          ) : (
            <CardRows
              rows={[
                {
                  label: t('service.reviewSubmit.device.materialNo'),
                  value: deviceData.materialNumber,
                },
                { label: t('service.reviewSubmit.device.serialNo'), value: deviceData.serialNumber },
              ]}
            />
          )}
        </Card>

        {/* Issue */}
        <Card className="review-submit__card" title={t('service.reviewSubmit.issue.title')}>
          {issueDescriptionData.issueDescription && (
            <p className="review-submit__issue-description">{issueDescriptionData.issueDescription}</p>
          )}
          {issueDescriptionData.issuePhotos && issueDescriptionData.issuePhotos.length > 0 && (
            <div className="review-submit__photos">
              {issueDescriptionData.issuePhotos.map(photo => (
                <Image
                  className="review-submit__issue-photo"
                  wrapperClassName="review-submit__issue-photo-wrapper"
                  key={photo.uid}
                  src={photo.originFileObj ? URL.createObjectURL(photo.originFileObj) : photo.thumbUrl ?? ''}
                  alt={photo.name}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Customer & Location */}
        <Card className="review-submit__card" title={t('service.reviewSubmit.customerLocation.title')}>
          <CardRows
            rows={[
              { label: t('service.reviewSubmit.customerLocation.customer'), value: issueDetailsData.company },
              {
                label: t('service.reviewSubmit.customerLocation.deviceAddress'),
                value: deviceAddress,
              },
              {
                label: t('service.reviewSubmit.customerLocation.factoryNumber'),
                value: issueDetailsData.factoryNumber || t('common.NA'),
              },
            ]}
          />
        </Card>

        {/* Contact Details */}
        <Card className="review-submit__card" title={t('service.reviewSubmit.contact.title')}>
          <CardRows
            title={issueDetailsData.contactPerson === false ? t('service.reviewSubmit.contact.reportedBy') : undefined}
            rows={[
              {
                label: t('service.reviewSubmit.contact.name'),
                value: `${issueDetailsData.firstName} ${issueDetailsData.lastName}`.trim(),
              },
              { label: t('service.reviewSubmit.contact.email'), value: issueDetailsData.email },
              {
                label: t('service.reviewSubmit.contact.phone'),
                value: `${issueDetailsData.countryCode} ${issueDetailsData.phoneNumber}`,
              },
            ]}
          />
          {issueDetailsData.contactPerson === false && (
            <>
              <Divider className="review-submit__contact-divider" />
              <CardRows
                title={t('service.reviewSubmit.contact.onSiteContact')}
                rows={[
                  {
                    label: t('service.reviewSubmit.contact.name'),
                    value: `${issueDetailsData.contactFirstName} ${issueDetailsData.contactLastName}`.trim(),
                  },
                  {
                    label: t('service.reviewSubmit.contact.email'),
                    value: issueDetailsData.contactEmail,
                  },
                  {
                    label: t('service.reviewSubmit.contact.phone'),
                    value: `${issueDetailsData.contactCountryCode} ${issueDetailsData.contactPhoneNumber}`,
                  },
                ]}
              />
            </>
          )}
        </Card>

        {/* Purchase Order */}
        <Card className="review-submit__card" title={t('service.reviewSubmit.purchaseOrder.title')}>
          <CardRows
            rows={[
              {
                label: t('service.reviewSubmit.purchaseOrder.number'),
                value: issueDetailsData.purchaseOrderNumber || t('common.NA'),
              },
            ]}
          />
        </Card>

        <Checkbox
          className="review-submit__checkbox"
          checked={accepted}
          onChange={(e): void => setAccepted(e.target.checked)}
        >
          {t('service.reviewSubmit.dataProtection.checkboxLabel1')}{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e): void => e.stopPropagation()}
            className="review-submit__data-protection-link"
          >
            {t('service.reviewSubmit.dataProtection.dataProtectionPolicy')}
          </a>{' '}
          {t('service.reviewSubmit.dataProtection.checkboxLabel2')}
        </Checkbox>
      </Flex>

      <Flex align="center" className="review-submit__actions">
        <KccButton
          className="review-submit__back-btn"
          type="link"
          onClick={handleAddServiceCaseBack}
          icon={<SvgIcon name="leftArrow" className="review-submit__back-btn-icon" />}
          iconPosition="start"
        >
          {t('common.back')}
        </KccButton>
        <KccButton
          className="review-submit__submit-btn"
          type="primary"
          disabled={!accepted}
          onClick={handleSubmitRequest}
        >
          {t('service.reviewSubmit.submitButton')}
        </KccButton>
      </Flex>
    </StyledReviewSubmit>
  );
};
